import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const LoginForm = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      await login(formData.username, formData.password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="flex flex-col items-center justify-center w-full max-w-md p-6 mx-auto bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md transition-colors">
      <div className="w-full">
        <h2 className="mb-6 text-3xl px-10 sm:px-20 font-bold text-center text-ui-light-text-primary dark:text-ui-dark-text-primary">
          Sign In
        </h2>
        
        {error && (
          <div className="p-3 mb-4 text-sm text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-lg">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary" htmlFor="username">
              Username
            </label>
            <input
              id="username"
              name="username"
              type="text"
              autoComplete="username"
              required
              value={formData.username}
              onChange={handleChange}
              className="w-full px-3 py-2 text-ui-light-text-primary dark:text-ui-dark-text-primary border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-hover focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
              placeholder="Your username"
            />
          </div>
          
          <div className="mb-6">
            <label className="block mb-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={formData.password}
              onChange={handleChange}
              className="w-full px-3 py-2 text-ui-light-text-primary dark:text-ui-dark-text-primary border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-hover focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400"
              placeholder="Your password"
            />
          </div>
          
          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-4 py-2 text-sm font-medium text-white dark:text-neutral-100 bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-neutral-800 disabled:opacity-50 transition-colors"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="w-5 h-5 mr-3 -ml-1 text-white dark:text-neutral-100 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </div>
        </form>
        
        <div className="mt-4 text-center">
          <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
            Don't have an account?{' '}
            <Link to="/register" className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
              Sign up
            </Link>
          </p>
          <p className="mt-2 text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
            <Link to="/reset-password" className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors">
              Forgot your password?
            </Link>
          </p>
        </div>
        
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-ui-light-border dark:border-ui-dark-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary bg-ui-light-card dark:bg-ui-dark-card">
                Or try without an account
              </span>
            </div>
          </div>
          
          <div className="mt-6">
            <Link
              to="/query"
              className="flex justify-center w-full px-4 py-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary bg-ui-light-hover dark:bg-ui-dark-hover border border-transparent rounded-md hover:bg-ui-light-borderHover dark:hover:bg-ui-dark-borderHover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
            >
              Continue as Guest
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;